import Vue from 'vue';
import axios from 'axios';
import orderBy from 'lodash/orderBy';
import pullAll  from 'lodash/pullAll';
import flatMap  from 'lodash/flatMap';
import find  from 'lodash/find';

import { Plugins, FilesystemDirectory, FilesystemEncoding } from '@capacitor/core';
import { BASE_URL } from '@/constants/constants';

const { Storage, Filesystem, Device } = Plugins;

const rootUrl = BASE_URL;

export default {
    state: {
        articles: [],
        list: [],
        old_list: [],
        new_list: [],
        subscription_exclusive: false,
        queue: 0,
        featured: [],
        articles_synced_on: null,
        articles_syncing: false,
        skip: []
    },

    mutations: {
        setArticlesList(state, data) {
            // set list in store
            state.list = orderBy(data, 'last_modified', 'desc');
            // commit to localStorage
            Storage.set({
                key: 'articles_list',
                value: JSON.stringify(state.list)
            });
        },

        clearArticlesList(state) {
            // set list in store
            state.list = [];
            // commit to localStorage
            Storage.set({
                key: 'articles_list',
                value: JSON.stringify(state.list)
            });
        },

        setArticles(state, data) {
            // setting list of articles that have been fetched
            state.articles = data;
            Storage.set({
                key: 'articles',
                value: JSON.stringify(state.articles)
            });
        },

        setArticlesSyncing(state, data) {
            state.articles_syncing = data;
        },

        setSubscriptionExclusive(state, data) {
            state.subscription_exclusive = data;
        },

        setQueue(state, data) {
            state.queue = data;
        },

        setArticleSyncingNow(state, data) {
            state.article_syncing_now = data;
        },

        addToSkip(state, data) {
            const list = state.skip.slice();
            list.push(data);
            Vue.set(state, 'skip', list);
        },

        setSkip(state, data) {
            Vue.set(state, 'skip', data);
        },

        updateArticlesSyncedOn(state,data) {
            state.articles_synced_on = Date.now();
        },

        updateArticles(state, data) {
            // setting list of articles that have been fetched
            const index = state.articles.findIndex((item) => {
                return item.article_id === data.article_id;
            });
            if (index > -1) {
                Vue.set(state.articles, index, data);
            } else {
                state.articles.push(data);
            }
            Storage.set({
                key: 'articles',
                value: JSON.stringify(state.articles)
            });
        },

        setNewArticlesList(state, data) {
            // set new_list that's used to compare w/ old_list for article content updates
            state.new_list = orderBy(data, 'last_modified', 'desc');
        },

        setOldArticlesList(state, data) {
            // set old_list that's used to compare w/ new_list for article content updates
            state.old_list = orderBy(data, 'last_modified', 'desc');
        },

        setFeaturedArticles(state, data) {
            // set the featured articles shown on the tablet/desktop home page
            state.featured = data;
            // commit to localStorage
            Storage.set({
                key: 'featured_articles',
                value: JSON.stringify(state.featured)
            });
        }
    },

    actions: {
        getArticlesData({ commit, dispatch }) {
            // gets saved article content list
            Storage.get({ key: 'articles' }).then((response) => {
                let list = JSON.parse(response.value);
                if (list && list.length) {
                    commit('setArticles', list);
                }
            });
            // gets articles_list from local storage
            Storage.get({ key: 'articles_list' }).then((response) => {
                let list = JSON.parse(response.value);
                if (list && list.length) {
                    commit('setArticlesList', list);
                    commit('setOldArticlesList', list);
                }
                dispatch('fetchArticles');
            });
            // gets featured_articles from local storage
            Storage.get({ key: 'featured_articles' }).then((response) => {
                let list = JSON.parse(response.value);
                if (list && list.length) {
                    commit('setFeaturedArticles', list);
                }
                dispatch('fetchFeaturedArticles');
            });
        },

        async fetchMissingArticles({ state, commit, dispatch, getters, rootState }) {
            const syncing = getters.getArticlesSyncing;
            if (syncing) { return true; }
            await commit('setArticlesSyncing', true);
            await dispatch('fetchMissingArticle');
            await commit('setArticlesSyncing', false);
            return true;
        },

        async fetchMissingArticle({ state, commit, dispatch, getters, rootState }) {

            let missing = getters.getMissingArticles;
            let skip = getters.getArticleSkip;

            missing = missing.filter((list) => {
                return !skip.includes(list.id);
            });

            if (missing.length > 0) {
                let article = missing[0];
                try {
                    const data = {
                        article_id: article.id,
                        subscription_id: rootState.subscriptions.active_subscription.id,
                        prefix: article.prefix_url
                    }
                    await dispatch('fetchArticleData', data);
                } catch (err) {
                    commit('addToSkip', article.id);
                }
                await dispatch('fetchMissingArticle');
            }

            return true;
        },

        async fetchArticles({ state, commit, dispatch, rootState }) {
            let response;
            let headers = { 'Authorization': rootState.authorization };
            try {
                response = await axios.get(`${rootUrl}/account/application/2/article`, { headers: headers });
            } catch (err) {
                console.error(err);
                return;
            }

            let newArticles = [];
            const list = response.data;

            commit('setArticlesList', list);

            if (state.old_list && state.old_list.length) {
                for ( const index in list ) {
                    const article = list[index];
                    const oldItem = state.list.find((oldArticle) => {
                        return article.id === oldArticle.id;
                    });
                    if (!oldItem) {
                        newArticles.push(article);
                    }
                    const needsUpdated = oldItem.last_deployed < article.last_deployed;
                    if ( needsUpdated ) {
                        newArticles.push(article);
                        let items = state.articles.filter((item) => {
                            return item.article_id !== article.id;
                        });
                        commit('setArticles', items);
                    }
                }
                await commit('setNewArticlesList', newArticles);
                await dispatch('fetchAllArticles');
                return list;
            } else {
                await dispatch('fetchAllArticles');
            }

            return list;
        },

        async fetchAllArticles({ state, dispatch, commit }) {
            // loop through items in recently fetched article list until complete
            if ( state.articles_syncing ) { return true; }
            commit('setArticlesSyncing', true);
            await dispatch('checkSubscriptionCache');
            commit('setArticlesSyncing', false);
            commit('updateArticlesSyncedOn');
            return true;
        },

        async fetchArticle({ state, dispatch, commit }) {
            const list = state.new_list;
            if (list.length > 0) {
                const article = list.shift();
                commit('setNewArticlesList', list);
                let data = {
                    article_id: article.id,
                    subscriptions: article.subscriptions,
                    subscription_index: 0,
                    prefix: article.prefix_url
                };
                await dispatch('fetchArticleVersions', data);
                if (list.length > 0) {
                    await dispatch('fetchArticle');
                }
                return article;
            }
        },

        async fetchArticleVersions({ state, dispatch, commit, rootState }, data) {
            // loop through subscriptions in article
            for (const subscription in data.subscriptions) {
                const sub = data.subscriptions[subscription];
                let requestData = {
                    article_id: data.article_id,
                    subscription_id: sub,
                    prefix: data.prefix
                };
                await dispatch('fetchArticleData', requestData);
            }
        },

        async checkSubscriptionCache({ state, dispatch, commit, getters, rootState}) {
            if ( state.articles_syncing ) { return true; }
            const info = await Device.getInfo();
            commit('setArticlesSyncing', true);
            dispatch('fetchArticles');

            const list = getters.getSubArticles;
            const sub = rootState.subscriptions.active_subscription.id;

            console.info('Verifying article cache...');

            for ( const index in list ) {
                const article = list[index];
                const article_name = `article-${article.id}-${sub}.html`;
                try {
                    // Check that the file exists
                    const stat = await Filesystem.stat({
                        path: article_name,
                        directory:  FilesystemDirectory.Cache
                    });
                    const server = ( info.platform === 'web' ) ? article.last_deployed * 1000 : article.last_deployed;
                    const local = stat.mtime;
                    if (server > local ) {
                        console.info(`Article ${article.slug} (${article.id}) needs to be updated.`);
                        console.info(`${article.id}: ${server} vs ${local}`);
                        let items = state.articles.filter((item) => {
                            return item.article_id !== article.id;
                        });
                        commit('setArticles', items);
                    }
                } catch (err) {
                    console.error(`${article_name} was not cached.`);
                    let items = state.articles.filter((item) => {
                        return item.article_id !== article.id;
                    });
                    commit('setArticles', items);
                }
            }
            commit('setArticlesSyncing', false);
            dispatch('fetchMissingArticles');
        },

        async fetchArticleData({ state, rootState, commit }, data) {
            // fetch article content from api
            // let headers = { 'Authorization': rootState.authorization };
            try {
                const article_url = `${data.prefix}${data.subscription_id}.html`;
                const response = await axios.get(article_url);
                const article_name = `article-${data.article_id}-${data.subscription_id}.html`;
                const contents = response.data;

                await Filesystem.writeFile({
                    path: article_name,
                    data: contents,
                    directory:  FilesystemDirectory.Cache,
                    encoding: FilesystemEncoding.UTF8
                });

                const date = new Date();
                const timestamp = date.getTime();

                const content = {
                    timestamp,
                    content: article_name,
                    subscription_id: data.subscription_id,
                    article_id: data.article_id,
                    contents: contents
                };

                commit('updateArticles', {
                    article_id: data.article_id,
                    synced: timestamp,
                    error: false
                });

                return content;

            } catch (err) {
                const date = new Date();
                const timestamp = date.getTime();
                commit('updateArticles', {
                    article_id: data.article_id,
                    synced: false,
                    error: timestamp
                });
                return false;
            }
        },

        async fetchFeaturedArticles({ rootState, commit }, data) {
            let headers = {
                'Authorization': rootState.authorization
            };
            try {
                const response = await axios.get(`${rootUrl}/account/application/2/article?featured=1`, { headers: headers });
                commit('setFeaturedArticles', response.data);
                return response.data;
            } catch (err) {
                console.error(err);
                return false;
            }
        },

        clearArticlesData({ state, commit }) {
            // clear saved articles from vuex store
            commit('setArticles', []);
            commit('setArticlesList', []);
            commit('setNewArticlesList', []);
            commit('setOldArticlesList', []);
            commit('setFeaturedArticles', []);
        },

        // used to get article content from an article page
        async getArticleContent({ dispatch, commit }, data) {
            const article_name = `article-${data.article_id}-${data.subscription_id}.html`;
            try {
                const contents = await Filesystem.readFile({
                    path: article_name,
                    directory:  FilesystemDirectory.Cache,
                    encoding: FilesystemEncoding.UTF8
                });
                commit('updateArticles', { article_id: data.article_id });
                return contents;
            } catch (err) {
                console.error(`${article_name} does not exist. Loading...`);
                const response = await dispatch('fetchArticleData', data);
                commit('updateArticles', { article_id: data.article_id });
                return { data: response.contents };
            }
        },
    },

    getters: {
        getArticleList: (state, getters, rootState) => (id) => {
            // get the article list for the current page
            let items = state.list.filter((list) => {
                let parent_topic = list.parent_topic === id;
                let active_subscription = list.subscriptions.includes(rootState.subscriptions.active_subscription.id);
                return active_subscription && parent_topic;
            });

            // sort by order
            items.sort((a, b) => {
                return a.order - b.order;
            });

            // further sort alphabetically by order
            items.sort((a, b) => {
                let comparison = 0;

                if (a.order === b.order) {
                    if (a.slug > b.slug) {
                        comparison = 1;
                    } else {
                        comparison = -1;
                    }
                } else if (a.order - b.order) {
                    comparison = 1;
                } else {
                    comparison = -1;
                }

                return comparison;
            });

            return items;
        },

        getArticleGroup: (state, getters, rootState) => (id) => {
            // get a specific article group
            return state.list.find((article) => {
                return article.id === id && article.subscriptions.includes(rootState.subscriptions.active_subscription.id);
            });
        },

        getArticle: (state, getters, rootState) => (slug) => {
            // get a specific article
            return state.list.find((article) => {
                return article.slug === slug && article.subscriptions.includes(rootState.subscriptions.active_subscription.id);
            });
        },

        getArticlesSyncedOn: (state) => {
            return state.articles_synced_on;
        },

        getArticles: (state, getters, rootState) => {
            let items = state.list.filter((list) => {
                return !list.hide_in_list && list.subscriptions.includes(rootState.subscriptions.active_subscription.id);
            });
            return items;
        },

        getSubArticles: (state, getters, rootState) => {
            let items = state.list.filter((list) => {
                return list.subscriptions.includes(rootState.subscriptions.active_subscription.id);
            });
            return items;
        },

        getSubFeaturedArticles: (state, getters, rootState) => {
            let items = state.list.filter((list) => {
                return list.is_featured === true && list.subscriptions.includes(rootState.subscriptions.active_subscription.id);
            });
            return items;
        },

        getAllArticles: (state) => {
            return state.list;
        },

        getArticleCache: (state, getters) => {
            let articles = getters.getSubArticles;
            let items = state.articles.filter((list) => {
                return find(articles, {'id': list.article_id });
            });
            return items;
        },

        getMissingArticles: (state, getters) => {
            let articles = getters.getSubArticles;
            let items = articles.filter((list) => {
                return !find(state.articles, { 'article_id': list.id });
            });
            return items;
        },

        getErrorArticles: (state, getters) => {
            let articles = getters.getSubArticles;
            let items = articles.filter((list) => {
                return !find(state.articles, { 'article_id': list.id, 'error': false });
            });
            return items;
        },

        getArticlesSyncing: (state) => {
            return state.articles_syncing;
        },

        getArticleSkip: (state) => {
            return state.skip;
        }
    }
};
